/**
 * ATLETAHUB — GLOBAL ERROR & UNHANDLED REJECTION HANDLERS (HEO-IMPLEMENT-3B)
 * 
 * Captura exceções no nível da janela do navegador (window.onerror e window.onunhandledrejection),
 * aplicando sanitização estrita, mitigação de loops e integração com observabilidade.
 */

import { sanitizeErrorMessage } from "../health/ErrorNormalizationService";
import { LoggerService, sanitizeSensitiveData } from "../logging/LoggerService";

interface ErrorFingerprintEntry {
  timestamp: number;
  count: number;
}

// Controle anti-loop e anti-spam em memória
const recentErrors = new Map<string, ErrorFingerprintEntry>();
const DEDUPLICATION_WINDOW_MS = 3000; // Janela de 3 segundos para deduplicação
const MAX_TRACKED_ERRORS = 100;

let isInstalled = false;
let errorHandlerReference: ((event: ErrorEvent) => void) | null = null;
let rejectionHandlerReference: ((event: PromiseRejectionEvent) => void) | null = null;
let originalConsoleError: ((...args: any[]) => void) | null = null;
let originalConsoleWarn: ((...args: any[]) => void) | null = null;
let isSendingToObservability = false;

function isViteOrWebSocketNoise(msg: string): boolean {
  const lower = (msg || "").toLowerCase();
  return (
    lower.includes("failed to connect to websocket") ||
    lower.includes("websocket closed without opened") ||
    lower.includes("[vite]") ||
    lower.includes("__vite_ping") ||
    lower.includes("ws://") ||
    lower.includes("wss://")
  );
}

async function reportFrontendErrorToBackend(payload: { message: string; stack?: string; route?: string; errorType?: string }) {
  if (isSendingToObservability || typeof window === "undefined") return;
  isSendingToObservability = true;
  try {
    await fetch("/api/observability/frontend-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    // Silently ignore network failures when reporting errors to avoid error loops
  } finally {
    isSendingToObservability = false;
  }
}

/**
 * Verifica se um erro recente já foi processado recentemente para evitar floods de console e loops.
 */
function shouldThrottle(fingerprint: string): boolean {
  const now = Date.now();

  // Limpeza de cache se atingir limite
  if (recentErrors.size > MAX_TRACKED_ERRORS) {
    for (const [key, val] of Array.from(recentErrors.entries())) {
      if (now - val.timestamp > DEDUPLICATION_WINDOW_MS * 2) {
        recentErrors.delete(key);
      }
    }
  }

  const existing = recentErrors.get(fingerprint);
  if (!existing) {
    recentErrors.set(fingerprint, { timestamp: now, count: 1 });
    return false;
  }

  if (now - existing.timestamp < DEDUPLICATION_WINDOW_MS) {
    existing.count += 1;
    return true;
  }

  // Renova a janela
  recentErrors.set(fingerprint, { timestamp: now, count: 1 });
  return false;
}

/**
 * Inicializa os ouvintes globais de erro no navegador.
 */
export function setupGlobalErrorHandlers(): () => void {
  if (typeof window === "undefined" || isInstalled) {
    return () => {};
  }

  errorHandlerReference = (event: ErrorEvent) => {
    try {
      const rawMessage = event.message || event.error?.message || "Erro desconhecido em runtime";
      
      if (isViteOrWebSocketNoise(rawMessage)) {
        return; // Ignora ruído de HMR / WebSocket do Vite (Block 5)
      }

      const sanitized = sanitizeErrorMessage(rawMessage);
      const fingerprint = `err_${sanitized}_${event.lineno}_${event.colno}`;

      if (shouldThrottle(fingerprint)) {
        return;
      }

      // Log estruturado e seguro
      LoggerService.error(
        "window.error",
        `[RuntimeError] ${sanitized}`,
        {
          lineno: event.lineno,
          colno: event.colno,
          timestamp: new Date().toISOString(),
        }
      );

      // Envio para a Central de Saúde 2.0 (Block 4)
      reportFrontendErrorToBackend({
        message: sanitized,
        stack: event.error?.stack ? String(event.error.stack) : undefined,
        route: window.location?.pathname || "/",
        errorType: "RuntimeError",
      });
    } catch {
      // Proteção total contra exceções secundárias
    }
  };

  rejectionHandlerReference = (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason;
      let rawMessage = "Promise rejeitada sem motivo explícito";

      if (typeof reason === "string") {
        rawMessage = reason;
      } else if (reason?.message) {
        rawMessage = reason.message;
      } else if (reason && typeof reason === "object") {
        try {
          rawMessage = JSON.stringify(reason);
        } catch {
          rawMessage = String(reason);
        }
      }

      if (isViteOrWebSocketNoise(rawMessage)) {
        return; // Ignora ruído de HMR / WebSocket do Vite (Block 5)
      }

      const sanitized = sanitizeErrorMessage(rawMessage);
      const fingerprint = `rej_${sanitized}`;

      if (shouldThrottle(fingerprint)) {
        return;
      }

      LoggerService.warn(
        "window.unhandledrejection",
        `[UnhandledRejection] ${sanitized}`,
        {
          timestamp: new Date().toISOString(),
        }
      );

      // Envio para a Central de Saúde 2.0 (Block 4)
      reportFrontendErrorToBackend({
        message: sanitized,
        stack: reason?.stack ? String(reason.stack) : undefined,
        route: window.location?.pathname || "/",
        errorType: "UnhandledRejection",
      });
    } catch {
      // Proteção total contra exceções secundárias
    }
  };

  window.addEventListener("error", errorHandlerReference);
  window.addEventListener("unhandledrejection", rejectionHandlerReference);

  // Sanitização de console.error e console.warn contra vazamento acidental de tokens/segredos
  if (typeof console !== "undefined") {
    if (!originalConsoleError && typeof console.error === "function") {
      originalConsoleError = console.error.bind(console);
      console.error = (...args: any[]) => {
        try {
          const sanitizedArgs = args.map((arg) => sanitizeSensitiveData(arg));
          originalConsoleError!(...sanitizedArgs);
        } catch {
          if (originalConsoleError) originalConsoleError(...args);
        }
      };
    }

    if (!originalConsoleWarn && typeof console.warn === "function") {
      originalConsoleWarn = console.warn.bind(console);
      console.warn = (...args: any[]) => {
        try {
          const sanitizedArgs = args.map((arg) => sanitizeSensitiveData(arg));
          originalConsoleWarn!(...sanitizedArgs);
        } catch {
          if (originalConsoleWarn) originalConsoleWarn(...args);
        }
      };
    }
  }

  isInstalled = true;

  return teardownGlobalErrorHandlers;
}

/**
 * Remove os manipuladores globais (útil para suíte de testes).
 */
export function teardownGlobalErrorHandlers(): void {
  if (typeof window === "undefined" || !isInstalled) {
    return;
  }

  if (errorHandlerReference) {
    window.removeEventListener("error", errorHandlerReference);
    errorHandlerReference = null;
  }

  if (rejectionHandlerReference) {
    window.removeEventListener("unhandledrejection", rejectionHandlerReference);
    rejectionHandlerReference = null;
  }

  if (originalConsoleError && typeof console !== "undefined") {
    console.error = originalConsoleError;
    originalConsoleError = null;
  }

  if (originalConsoleWarn && typeof console !== "undefined") {
    console.warn = originalConsoleWarn;
    originalConsoleWarn = null;
  }

  recentErrors.clear();
  isInstalled = false;
}
